import { useMemo, useState } from "react"
import { useFreeProducts } from "../../api/queries"
import { useEscape } from "../../hooks/useEscape"

const SUGGESTIONS = 15

/** Строка подбора товара: добавление в карточку и режим замены.
 *  Список свободных товаров приходит целиком и фильтруется здесь же. */
export default function ProductPicker({
	languageId,
	priceGroupId,
	busy,
	taken,
	replacing,
	onPick,
	onCancelReplace,
}: {
	languageId: number
	priceGroupId: number | null
	busy: boolean
	taken: string[]
	replacing: string | null
	onPick: (productId: string) => void
	onCancelReplace: () => void
}) {
	const [query, setQuery] = useState("")
	const free = useFreeProducts(priceGroupId, languageId, true)

	const matches = useMemo(() => {
		const needle = query.trim().toLowerCase()
		if (!needle) return []
		return (free.data ?? [])
			.filter(
				product =>
					!taken.includes(product.id) &&
					(product.id.toLowerCase().includes(needle) ||
						product.name.toLowerCase().includes(needle))
			)
			.slice(0, SUGGESTIONS)
	}, [free.data, query, taken])

	const pick = (productId: string) => {
		setQuery("")
		onPick(productId)
	}

	// Esc в два шага: сначала убирает подсказки, потом выходит из режима замены.
	// Список остаётся на экране и без фокуса в поле, поэтому слушаем окно
	useEscape(!!replacing || query !== "", () => {
		if (query) setQuery("")
		else onCancelReplace()
	})

	return (
		<div className="tp-picker">
			<span className="tp-picker__label">
				{replacing ? `Заменить ${replacing} на` : "Добавить"}
			</span>
			<div className="tp-picker__field">
				<input
					className="input"
					placeholder="Артикул или название"
					value={query}
					disabled={busy}
					autoFocus={!!replacing}
					onChange={event => setQuery(event.target.value)}
					onKeyDown={event => {
						if (event.key !== "Enter") return
						// точный артикул добавляется сразу, как в ECM
						const exact = (free.data ?? []).find(
							product => product.id.toLowerCase() === query.trim().toLowerCase()
						)
						if (exact) pick(exact.id)
						else if (matches.length === 1) pick(matches[0].id)
					}}
				/>
				{matches.length > 0 && (
					<div className="tp-picker__list">
						{matches.map(product => (
							<button
								key={product.id}
								className="tp-picker__item"
								disabled={busy}
								onClick={() => pick(product.id)}
							>
								<span className="mono muted">{product.id}</span>
								<span className="tp-picker__name">{product.name}</span>
							</button>
						))}
					</div>
				)}
			</div>
			{replacing && (
				<button
					className="btn"
					title="Выйти из режима замены (Esc)"
					onClick={onCancelReplace}
				>
					Отмена
				</button>
			)}
			<span className="muted hint-inline">
				{free.isLoading
					? "список товаров загружается…"
					: `свободных товаров: ${(free.data ?? []).length}`}
			</span>
		</div>
	)
}
